/**
 * «Не прошло — как выйти»: окно выхода из несведённой вилки с Polymarket.
 *
 * Человек говорит, что у него стоит — шары Polymarket или ставка в конторе, —
 * окно считает выходы по живому стакану (раз в 3 с, пока открыто) и
 * котировкам других контор. Сверху лучший по худшему исходу. Без сервера,
 * кроме уже существующего списка перекрытий.
 */

import { Decimal } from 'decimal.js';
import { useEffect, useState } from 'react';

import { type CoverQuote, fetchCovers } from '../lib/cover';
import { type ExitInput, exitSay, exitWays, type Level } from '../lib/exit';
import type { RowLeg } from '../lib/row';
import type { Meta } from './meta';
import { venueName } from './meta';
import { isPolymarket } from './order';
import { bookOf, tokensOf } from './polyBook';

const POLL_MS = 3000;

export type Kind = 'shares' | 'bet';

const num = (form: string): Decimal | null => {
  try {
    const v = new Decimal(form.trim().replace(',', '.'));
    return v.isFinite() && v.gt(0) ? v : null;
  } catch {
    return null;
  }
};

interface Books {
  mine: { bids: Level[]; asks: Level[] } | null;
  other: { asks: Level[] } | null;
}

export function ExitPane({
  forkId,
  legs,
  meta,
  fx,
  got,
  kick,
}: {
  forkId: string;
  legs: readonly RowLeg[];
  meta: Meta;
  fx: number;
  /** Шары, которые кошелёк реально получил (налив по кошельку). */
  got?: string | null;
  /** Открыть окно снаружи: `n` растёт на каждое нажатие, `kind` — что стоит. */
  kick?: { n: number; kind: Kind };
}): React.ReactElement | null {
  const poly = legs.find((leg) => isPolymarket(leg.source));
  const book = legs.find((leg) => !isPolymarket(leg.source));
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind>(legs[0] === poly ? 'shares' : 'bet');
  const [shares, setShares] = useState('');
  const [price, setPrice] = useState('');
  const [stake, setStake] = useState('');
  const [odds, setOdds] = useState('');
  const [books, setBooks] = useState<Books>({ mine: null, other: null });
  const [covers, setCovers] = useState<CoverQuote[]>([]);

  const failed = kind === 'shares' ? book : poly;

  const polyUrl = poly?.url ?? null;
  const polyAsset = poly?.asset ?? null;
  useEffect(() => {
    if (!open || (polyUrl === null && polyAsset === null)) return;
    let gone = false;
    const pull = async (): Promise<void> => {
      const tokens = await tokensOf({ url: polyUrl, asset: polyAsset });
      if (tokens === null || gone) return;
      const [mine, other] = await Promise.all([
        bookOf(tokens.mine),
        tokens.other !== null ? bookOf(tokens.other) : Promise.resolve(null),
      ]);
      if (!gone) setBooks({ mine, other: other === null ? null : { asks: other.asks } });
    };
    void pull();
    const timer = setInterval(() => void pull(), POLL_MS);
    return () => {
      gone = true;
      clearInterval(timer);
    };
  }, [open, polyUrl, polyAsset]);

  useEffect(() => {
    if (!open || failed === undefined) return;
    let gone = false;
    void fetchCovers(forkId, failed.side).then((res) => {
      if (!gone) setCovers(res.ok ? res.quotes : []);
    });
    return () => {
      gone = true;
    };
  }, [open, forkId, failed?.side]);

  const start = (as?: Kind): void => {
    if (poly === undefined || book === undefined) return;
    if (as !== undefined) setKind(as);
    setShares(got ?? (poly.shares ?? 0).toFixed(1));
    setPrice(((poly.cents ?? 0) / 100).toFixed(3));
    setStake(
      book.usd !== null ? book.usd.toFixed(2) : fx > 0 ? (book.rub / fx).toFixed(2) : '',
    );
    setOdds(book.odds);
    setOpen(true);
  };

  const kickN = kick?.n ?? 0;
  useEffect(() => {
    if (kickN > 0) start(kick?.kind);
    // Только на новое нажатие снаружи, не на каждый кадр ленты.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kickN]);

  if (poly === undefined || book === undefined || legs.length !== 2) return null;

  const n = num(shares);
  const p = num(price);
  const s = num(stake);
  const k = num(odds);
  const fee = poly.payoutModel?.kind === 'shares' ? poly.payoutModel.rate : undefined;
  const others = covers
    .filter((q) => q.unit !== 'share' && !isPolymarket(q.s) && q.s !== failed?.source)
    .map((q) => ({ source: q.s, odds: q.odds }));

  let input: ExitInput | null = null;
  if (kind === 'shares' && n !== null && p !== null) {
    input = {
      stand: { kind: 'shares', shares: n, cost: n.times(p) },
      ...(books.mine ? { bids: books.mine.bids } : {}),
      ...(books.other ? { oppAsks: books.other.asks } : {}),
      covers: others,
      retry: { source: book.source, odds: book.odds },
      ...(fee !== undefined ? { feeRate: fee } : {}),
    };
  } else if (kind === 'bet' && s !== null && k !== null) {
    input = {
      stand: { kind: 'bet', source: book.source, stake: s, odds: k },
      ...(books.mine ? { asks: books.mine.asks } : {}),
      covers: others,
      ...(fee !== undefined ? { feeRate: fee } : {}),
    };
  }
  const ways = input !== null ? exitWays(input) : [];
  const nameOf = (code: string): string => venueName(meta, code);

  if (!open) {
    return (
      <div className="nx-exit">
        <button type="button" className="nx-det__miss" onClick={() => start()}>
          не прошло — как выйти
        </button>
      </div>
    );
  }

  return (
    <div className="nx-exit open">
      <div className="nx-exit__stand">
        <label>
          <input
            type="radio"
            checked={kind === 'shares'}
            onChange={() => setKind('shares')}
          />{' '}
          стоят шары Polymarket
        </label>
        <label>
          <input type="radio" checked={kind === 'bet'} onChange={() => setKind('bet')} /> стоит
          ставка в {nameOf(book.source)}
        </label>
        <button type="button" className="nx-det__miss" onClick={() => setOpen(false)}>
          закрыть
        </button>
      </div>
      <div className="nx-exit__form">
        {kind === 'shares' ? (
          <>
            <label>
              шаров <input inputMode="decimal" value={shares} onChange={(e) => setShares(e.target.value)} />
            </label>
            <label>
              средняя цена <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
            </label>
          </>
        ) : (
          <>
            <label>
              ставка $ <input inputMode="decimal" value={stake} onChange={(e) => setStake(e.target.value)} />
            </label>
            <label>
              кэф <input inputMode="decimal" value={odds} onChange={(e) => setOdds(e.target.value)} />
            </label>
          </>
        )}
      </div>
      {ways.length === 0 ? (
        <p className="nx-exit__none">
          {books.mine === null ? 'стакан Polymarket загружается…' : 'выхода по стакану и конторам нет'}
        </p>
      ) : (
        <>
          <p className="nx-exit__best">
            <b>{exitSay(ways[0]!, nameOf)}</b>
          </p>
          {ways.length > 1 && (
            <ul className="nx-exit__rest">
              {ways.slice(1).map((w) => (
                <li key={`${w.kind}-${w.source}`}>{exitSay(w, nameOf)}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
